/**
 * Strafer — the RUN-AND-GUN aerial gunship (step 4, last-2). The first FLYING shooter.
 *
 * A rigid gunship that cruises HORIZONTALLY at its placed altitude (no gravity), ping-ponging
 * within `props.range` px of its spawn x (and reversing at walls), and fires an ENEMY projectile
 * toward the player — quantized 8-way — after a short telegraph while the player is in range. A
 * light aerial: stomp-from-above kills+bounces (if you can reach it), a bullet kills it, any other
 * contact hurts.
 *
 * Determinism: integer step counter + abs + explicit-sign ternary + Math.SQRT2 constant; the shot
 * emits through ctx.spawnEntityAt's deterministic queue. No random / wall-clock / transcendentals.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';
import { PROJECTILE_H } from './Projectile';

const SQUASH_SECONDS = 0.4;
const DEFAULT_SPEED = 90;
const DEFAULT_RANGE = 256;    // patrol half-width around the spawn x
const DEFAULT_COOLDOWN = 80;
const DEFAULT_BULLET_SPEED = 300;
const DEFAULT_BULLET_LIFE = 220;
const DEFAULT_FIRE_RANGE = 820;

export class Strafer implements Entity {
  readonly type = EntityType.STRAFER;
  body: AABB;
  vx: number;
  vy = 0;
  facing: 1 | -1 = -1;
  readonly spriteFacesLeft = true;
  alive = true;
  squashTimer = 0;

  private speed: number;
  private readonly homeX: number;
  private patrolRange: number;
  private cooldown: number;
  private bulletSpeed: number;
  private bulletLife: number;
  private fireRangeSq: number;
  private sinceFire = 0;

  constructor(cx: number, feetY: number, props?: Record<string, unknown>, w = 56, h = 40) {
    this.body = { x: cx - w / 2, y: feetY - h, w, h };
    this.homeX = cx;
    this.speed = typeof props?.speed === 'number' ? props.speed : DEFAULT_SPEED;
    this.patrolRange = typeof props?.range === 'number' ? props.range : DEFAULT_RANGE;
    this.cooldown = typeof props?.fireCooldownSteps === 'number'
      ? Math.max(20, Math.floor(props.fireCooldownSteps)) : DEFAULT_COOLDOWN;
    this.bulletSpeed = typeof props?.bulletSpeed === 'number' ? props.bulletSpeed : DEFAULT_BULLET_SPEED;
    this.bulletLife = typeof props?.bulletLifeSteps === 'number'
      ? Math.floor(props.bulletLifeSteps) : DEFAULT_BULLET_LIFE;
    const fr = typeof props?.rangePx === 'number' ? props.rangePx : DEFAULT_FIRE_RANGE;
    this.fireRangeSq = fr * fr;

    const dir = props?.dir;
    if (dir === 1 || dir === '1' || dir === 'right') this.facing = 1;
    else if (dir === -1 || dir === '-1' || dir === 'left') this.facing = -1;
    this.vx = this.facing * this.speed;
  }

  update(dt: number, ctx: EntityContext): void {
    if (!this.alive) { this.squashTimer = Math.max(0, this.squashTimer - dt); return; }
    const grid = ctx.grid;

    // horizontal cruise; reverse at a wall OR the patrol-range edge (no gravity → stays aloft)
    this.vx = this.facing * this.speed;
    const cx = this.body.x + this.body.w / 2;
    if (grid.collideX(this.body, this.vx * dt) || Math.abs(cx - this.homeX) >= this.patrolRange) this.reverse();

    // fire an 8-way shot at an in-range player
    const scx = this.body.x + this.body.w / 2;
    const scy = this.body.y + this.body.h / 2;
    const ax = (ctx.player.body.x + ctx.player.body.w / 2) - scx;
    const ay = (ctx.player.body.y + ctx.player.body.h / 2) - scy;
    const inRange = ax * ax + ay * ay <= this.fireRangeSq;
    if (inRange) {
      this.sinceFire++;
      if (this.sinceFire >= this.cooldown) { this.fire(ctx, ax, ay); this.sinceFire = 0; }
    } else {
      this.sinceFire = 0;
    }
  }

  private fire(ctx: EntityContext, ax: number, ay: number): void {
    const absx = Math.abs(ax), absy = Math.abs(ay);
    const sx = ax < 0 ? -1 : 1;
    const sy = ay < 0 ? -1 : 1;
    let ux: number, uy: number;
    if (absx > absy * 2) { ux = sx; uy = 0; }
    else if (absy > absx * 2) { ux = 0; uy = sy; }
    else { ux = sx; uy = sy; }
    const len = ux !== 0 && uy !== 0 ? Math.SQRT2 : 1;
    const scx = this.body.x + this.body.w / 2;
    const scy = this.body.y + this.body.h / 2;
    ctx.spawnEntityAt(EntityType.PROJECTILE,
      scx + ux * (this.body.w * 0.3), scy + uy * (this.body.h * 0.4) + PROJECTILE_H / 2, {
        vx: (ux / len) * this.bulletSpeed, vy: (uy / len) * this.bulletSpeed,
        life: this.bulletLife, owner: 'enemy',
      });
    ctx.playSound('shoot', 0.3, 0.7); // POLISH_01 #1 — strafer shot
  }

  onPlayerOverlap(ctx: EntityContext): void {
    const p = ctx.player.body;
    const playerFeet = p.y + p.h;
    const fallingOnto = ctx.player.vy > 0 && playerFeet - this.body.y < this.body.h * 0.7;
    if (fallingOnto && ctx.offenseEnabled) {
      ctx.killEntity(this);
      ctx.bouncePlayer();
      ctx.playSound('stomp', 0.5); // POLISH_01 #1 — dedicated stomp clip
    } else {
      ctx.hurtPlayer();
    }
  }

  onKilled(): void {
    if (!this.alive && this.squashTimer > 0) return;
    this.alive = false;
    this.squashTimer = SQUASH_SECONDS;
    this.vx = 0;
  }

  damageable(): boolean { return true; } // stompable + shootable like a FLYER

  spriteKey(): string { return 'strafer_fly'; } // rigid gunship — motion is the animation
  visible(): boolean { return this.alive || this.squashTimer > 0; }

  private reverse(): void {
    this.facing = this.facing > 0 ? -1 : 1;
    this.vx = this.facing * this.speed;
  }
}
